import { useCallback, useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useFormik } from "formik";
import * as Yup from "yup";
import ReactPlayer from "react-player";
import PageHeader from "./PageHeader";
import SurfaceCard from "./SurfaceCard";
import { useFeedback } from "./FeedbackProvider";
import { addYoutubeLink, deletYoutubeLink, getVideos } from "firestoreService";

// Anything that is not a YouTube link renders as a dead black box on the home
// page, and nothing used to stop one being saved.
const YOUTUBE_URL = /^https:\/\/(www\.)?(youtube\.com\/(watch\?v=|shorts\/|embed\/)|youtu\.be\/)/;

const YouTube = () => {
  const { notify, confirm } = useFeedback();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await getVideos();
      // `res?.data` used to go straight into state, so a missing document put
      // `undefined` there and the next render threw on `.map`.
      setVideos(Array.isArray(result?.data) ? result.data : []);
    } catch (loadError) {
      console.error("Could not load videos:", loadError);
      setError("Could not load the video list.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const validation = useFormik({
    initialValues: { link: "" },
    validationSchema: Yup.object().shape({
      link: Yup.string()
        .trim()
        .required("Link is required")
        .matches(YOUTUBE_URL, "Enter a YouTube link (youtube.com/watch?v=… or youtu.be/…)"),
    }),
    onSubmit: async (values) => {
      try {
        await addYoutubeLink(values.link.trim());
        handleClose();
        notify("Video added.");
        load();
      } catch (submitError) {
        console.error("Could not add video:", submitError);
        notify("Could not add the video.", "error");
      }
    },
  });

  const handleClose = () => {
    validation.resetForm();
    setOpen(false);
  };

  const handleDelete = async (url) => {
    const confirmed = await confirm({
      title: "Remove this video?",
      message: "It will stop appearing on the public home page.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!confirmed) return;

    try {
      await deletYoutubeLink(url);
      notify("Video removed.");
      load();
    } catch (deleteError) {
      console.error("Could not remove video:", deleteError);
      notify("Could not remove the video.", "error");
    }
  };

  return (
    <>
      <PageHeader
        title="YouTube"
        description="Videos embedded on the public home page."
        actions={
          <>
            <Button
              onClick={load}
              startIcon={<RefreshIcon />}
              variant="outlined"
              sx={{ textTransform: "none" }}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", color: "#fff" }}
              onClick={() => setOpen(true)}
            >
              Add video
            </Button>
          </>
        }
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      <SurfaceCard>
        {loading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        ) : videos.length === 0 ? (
          <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
            <Typography sx={{ color: "#7b809a" }}>No videos published yet.</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{ textTransform: "none", color: "#fff" }}
              onClick={() => setOpen(true)}
            >
              Add the first one
            </Button>
          </Stack>
        ) : (
          <Grid container spacing={2}>
            {videos.map((url) => (
              <Grid item xs={12} sm={6} lg={4} key={url}>
                <Card
                  variant="outlined"
                  sx={{ borderRadius: 2, border: "1px solid #e3e8f0", overflow: "hidden" }}
                >
                  <Box sx={{ position: "relative", pt: "56.25%", bgcolor: "#000" }}>
                    <ReactPlayer
                      url={url}
                      width="100%"
                      height="100%"
                      controls
                      style={{ position: "absolute", top: 0, left: 0 }}
                    />
                  </Box>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ px: 1.5, py: 1 }}
                  >
                    <Typography
                      noWrap
                      sx={{ fontSize: "0.75rem", color: "#7b809a", minWidth: 0 }}
                      title={url}
                    >
                      {url}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(url)}
                      aria-label="Remove video"
                    >
                      <DeleteForeverIcon fontSize="small" sx={{ color: "#f44335" }} />
                    </IconButton>
                  </Stack>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </SurfaceCard>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pr: 6 }}>
          Add video
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 12, top: 12 }}
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={validation.handleSubmit} noValidate>
          <DialogContent dividers>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, mb: 0.5 }}>
              YouTube link
            </Typography>
            <TextField
              fullWidth
              size="small"
              name="link"
              placeholder="https://www.youtube.com/watch?v=..."
              value={validation.values.link}
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              error={Boolean(validation.touched.link && validation.errors.link)}
            />
            {validation.touched.link && validation.errors.link ? (
              <FormHelperText sx={{ color: "#f44335" }}>{validation.errors.link}</FormHelperText>
            ) : null}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ color: "#fff" }}
              disabled={validation.isSubmitting}
            >
              {validation.isSubmitting ? "Adding…" : "Add"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default YouTube;
